import React, { useState, useEffect, useContext  } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Button,
  Alert,
  Box,
  Stack,
  Card,
  CardContent,
  TextField,
  MenuItem,
  CircularProgress,
} from '@mui/material';
import documentoApi from '../../api/documentoApi';
import contribuenteApi from '../../api/contribuenteApi';
import logprocessoApi from '../../api/logprocessoApi';
import ProtectedAction from '../../components/ProtectedAction';
import { AuthContext } from '../../auth/AuthContext';
import { formatDate } from '../../utils/dateFormatter';

// Helper per estrarre messaggi di errore
const extractErrorMessage = (error) => {
  if (!error) return 'Errore sconosciuto';
  if (error.response?.data) return String(error.response.data);
  if(!error.response){
     return "L'elaborazione richiede più tempo del previsto. Attendi qualche minuto e riprova."+error.code+" "+error.message;
  }
  if(error.code === 'ECONNABORTED'){
       return "L'elaborazione richiede più tempo del previsto. Attendi qualche minuto e riprova."+error.code+" "+error.message;
  }
   if(error.code === 'ECONNRESET'){
        return "L'elaborazione richiede più tempo del previsto. Attendi qualche minuto e riprova."+error.code+" "+error.message;
   }
    if(error.message === 'Network Error'){
         return "L'elaborazione richiede più tempo del previsto. Attendi qualche minuto e riprova."+error.code+" "+error.message;
    }
  if (error.message) return error.message;
  return JSON.stringify(error);
};

export default function DocumentoUploadPageMobile() {
  const { id, contribuenteId } = useParams();
  const navigate = useNavigate();
  const isNew = !id;
  const hasContribuenteFromRoute = !!contribuenteId;
  const hasDocumentoFromRoute = !!id;
  const { user } = useContext(AuthContext);

  const [file, setFile] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [err, setErr] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [saving, setSaving] = useState(false);
  const [contribuenti, setContribuenti] = useState([]);
  const [selectedContribuente, setSelectedContribuente] = useState('');

  /* ========================= INIT CONTRIBUENTE ========================= */
  useEffect(() => {
    if (hasContribuenteFromRoute) setSelectedContribuente(Number(contribuenteId));
    else if (hasDocumentoFromRoute) {
      documentoApi.get(id)
        .then(r => setSelectedContribuente(Number(r.data.contribuenteId)))
        .catch(e => setErr(extractErrorMessage(e)));
    }
  }, [contribuenteId, hasContribuenteFromRoute, hasDocumentoFromRoute, id]);

  useEffect(() => {
    if (isNew && !hasContribuenteFromRoute) {
      contribuenteApi.list({ page: 0, size: 100 })
        .then(r => setContribuenti(r.data.content || []))
        .catch(e => setErr(extractErrorMessage(e)));
    }
  }, [isNew, hasContribuenteFromRoute]);

  /* ========================= FILE HANDLING ========================= */
  const handleFileChange = (e) => {
    if (e.target.files.length > 0) {
      setFile(e.target.files[0]);
      setPreviewData(null);
      setErr(null);
    }
  };

  const generatePreview = async () => {
    if (!file || !selectedContribuente) {
      setErr('Selezionare un file e un contribuente.');
      return;
    }
    setLoadingPreview(true);
    setErr(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('contribuenteId', selectedContribuente);
      const response = isNew
        ? await documentoApi.newpreviewupload(formData)
        : await documentoApi.previewupload(id, formData);
       const processoId = response.data.id;
       console.log("processoId",processoId);
       // Polling finché il processo non è completato
        let completed = false;
        while (!completed) {
          await new Promise(resolve => setTimeout(resolve, 1000)); // attesa 1s
          const statusResp = await logprocessoApi.trova(processoId);
          const processData = statusResp.data;
          if (processData.stato === 'COMPLETED') {
            completed = true;
            if (processData.esito === 'OK') {
              console.log("previewDAta",processData.testo);
              setPreviewData(processData.testo); // testo contiene DocumentoDTO JSON
            } else {
              setErr(processData.anomalie || 'Errore nel processo di upload');
            }
          }
        }
      //setPreviewData(response.data);
    } catch (e) {
      setErr(extractErrorMessage(e));
    } finally {
      setLoadingPreview(false);
    }
  };

  /* ========================= CONFIRM UPLOAD ========================= */
  const confirmUpload = async () => {
    if (!file) return;
    setSaving(true);
    setErr(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('contribuenteId', selectedContribuente);
      previewData.contribuenteId = selectedContribuente;
      const response = isNew
        ? await documentoApi.create(previewData)
        : await documentoApi.addPdf(id, previewData);
        console.log("creato documento")
      if(user.role==="CONTRIBUENTE" && contribuenteId)
        navigate(`/documenti/bycontribuente/${selectedContribuente}`);
      else
        navigate(`/contribuenti/${selectedContribuente}`);
    } catch (e) {
      setErr(extractErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  /* ========================= RENDER ========================= */
  return (
    <Container sx={{ mt: 2, mb: 4 }}>
          {loadingPreview && (
            <Box
              sx={{
                position: 'fixed',
                inset: 0,
                bgcolor: 'rgba(255,255,255,0.7)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1300,
              }}
            >
              <CircularProgress size={48}/>
            </Box>
          )}
      <Typography variant="h5" sx={{ mb: 2 }}>
        {isNew ? 'Crea Documento da PDF' : 'Aggiungi PDF al Documento'}
      </Typography>
<Alert severity="warning" sx={{ mb: 2 }}>Tipologie di documento caricabili in questa pagina: F24 (UNICO o RATEIZZAZIONE AVVISO BONARIO), RATEIZZAZIONE ADE, ROTTAMAZIONE, MULTA, ACCERTAMENTO TARI, SOLLECITO TARI, INTIMAZIONE DI PAGAMENTO SINGOLA RATA. I dati estratti dai documenti PDF sono solo indicativi e servono a fornire
                                        informazioni all utente soprattutto riguardo alle date delle scadenze fiscali da pagare, alle quali
                                        viene prestata la massima attenzione. Gli importi e i codici dei moduli di pagamento
                                       saranno presenti ed accurati nella misura in cui i file PDF caricati sono
                                       leggibile dal software OCR che li interpreta  (è indispensabile una risoluzione dei PDFF di almeno 300dpi, se sono scansionati). Si invita l utente a consultare sempre il documento originale
                                       che viene caricato nel fascicolo ed allegato alle scadenze</Alert>
      {err && <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert>}

      {isNew && !hasContribuenteFromRoute && (
        <TextField
          select
          fullWidth
          label="Seleziona Contribuente"
          value={selectedContribuente}
          onChange={(e) => setSelectedContribuente(Number(e.target.value))}
          sx={{ mb: 2 }}
        >
          {contribuenti.map(c => (
            <MenuItem key={c.id} value={c.id}>
              {c.nome} {c.cognome}
            </MenuItem>
          ))}
        </TextField>
      )}

      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <input
          accept="application/pdf"
          style={{ display: 'none' }}
          id="upload-file"
          type="file"
          onChange={handleFileChange}
        />
        <label htmlFor="upload-file">
          <Button variant="outlined" component="span">
            {file ? 'Cambia PDF' : 'Sfoglia PDF'}
          </Button>
        </label>
        {file && <Typography variant="body2">{file.name}</Typography>}
      </Stack>

      <Box sx={{ mb: 2 }}>
        <Button
          variant="contained"
          fullWidth
          onClick={generatePreview}
          disabled={!file || loadingPreview}
        >
          {loadingPreview ? 'Generando preview...' : 'Anteprima dati'}
        </Button>
      </Box>

      {previewData && (
        <Stack spacing={2}>
          <Card>
            <CardContent>
              <Typography variant="h6">Preview Documento</Typography>
              {previewData.descrizione && <Typography>Descrizione: {previewData.descrizione}</Typography>}
              {previewData.numeroDocumento && <Typography>Numero Documento: {previewData.numeroDocumento}</Typography>}
              {previewData.dataDocumento && <Typography>Data Documento: {previewData.dataDocumento}</Typography>}
              {previewData.enteRiscossione && <Typography>Ente: {previewData.enteRiscossione}</Typography>}
              {previewData.cfContribuente && <Typography>CF: {previewData.cfContribuente}</Typography>}
              {previewData.nomeContribuente && <Typography>Destinatario: {previewData.nomeContribuente}</Typography>}
              {previewData.indirizzoContribuente && <Typography>Indirizzo/Via: {previewData.indirizzoContribuente}</Typography>}
            </CardContent>
          </Card>

          {previewData.scadenze?.map((s, idx) => (
            <Card key={idx}>
              <CardContent>
                <Typography>{`Rata ${s.numerorata} - ${s.dataScadenza} - €${s.importo}`}</Typography>
                {s.descrizione && <Typography>Descrizione: {s.descrizione}</Typography>}
                {s.codiceModuloPagamento && <Typography>Codice pagamento: {s.codiceModuloPagamento}</Typography>}
              </CardContent>
            </Card>
          ))}

          <Button
            variant="contained"
            color="success"
            fullWidth
            onClick={confirmUpload}
            disabled={saving}
          >
            {saving ? 'Salvando...' : 'Conferma e Salva'}
          </Button>
        </Stack>
      )}
    </Container>
  );
}
